/**
 * Calculate average scores from votes, normalising star votes to 0-10 scale.
 * @param {Object} votes - { voterId: { targetUserId: score, submittedAt: number } }
 * @param {Object} submissions - { userId: { imageData, nickname, ... } }
 * @param {'slider'|'stars'} votingMode
 * @returns {Object} { userId: averageScore }
 */
export function calculateScores(votes, submissions, votingMode) {
  const scores = {};
  const targetIds = Object.keys(submissions || {});

  for (const targetId of targetIds) {
    let total = 0;
    let count = 0;

    for (const [voterId, voterVotes] of Object.entries(votes || {})) {
      if (voterId === targetId) continue; // skip self
      const rawScore = voterVotes[targetId];
      if (rawScore === undefined || typeof rawScore !== 'number') continue;

      // Normalise star votes (1-3) to 0-10 scale
      const score = votingMode === 'stars'
        ? Math.round((rawScore / 3) * 10 * 10) / 10
        : rawScore;

      total += score;
      count++;
    }

    scores[targetId] = count > 0
      ? Math.round((total / count) * 10) / 10
      : 0;
  }

  return scores;
}

/**
 * Detect ties — returns array of userId arrays (each group = tied players).
 * Only returns groups with more than one player.
 */
export function detectTie(scores) {
  const sorted = Object.entries(scores).sort(([, a], [, b]) => b - a);
  if (sorted.length < 2) return null;

  const topScore = sorted[0][1];
  const tied = sorted.filter(([, s]) => s === topScore).map(([id]) => id);

  return tied.length > 1 ? tied : null;
}

/**
 * Build a leaderboard sorted lowest to highest (for dramatic reveal).
 * @returns {Array<{ userId, nickname, score, imageData }>}
 */
export function buildLeaderboard(scores, submissions) {
  return Object.entries(scores)
    .map(([userId, score]) => ({
      userId,
      nickname: submissions[userId]?.nickname || 'Unknown',
      imageData: submissions[userId]?.imageData || null,
      score,
    }))
    .sort((a, b) => a.score - b.score); // lowest first for reveal
}

/**
 * Determine voting mode based on player count.
 * 3-5 players: slider (1-10), 6+: stars (1-3)
 */
export function getVotingMode(memberCount) {
  return memberCount >= 6 ? 'stars' : 'slider';
}
